import {Socket} from "socket.io-client";
import useStore from "../store.ts";
import {SocketListener} from "./SocketListener.ts";
import {Component} from "../types.ts";
import {LayoutManager} from "../layout/LayoutManager.ts";
import {UpdateQueueManager} from "./UpdateQueueManager.ts";
import type {UpdateContextHandle} from "../utils/UpdateContextRegistry.ts";
import {UpdateOrigin} from "../utils/UpdateContextRegistry.ts";
import type {ChannelManager} from "../channel/ChannelManager.ts";
import type {RuleManager} from "../rule/RuleManager.ts";
import type {PlaylistManager} from "../playlist/PlaylistManager.ts";
import {resolveEnvironmentUrl} from "../utils/EnvironmentUrl.ts";
import {ConnectionState} from "./ConnectionState.ts";
import {SocketCommandQueue} from "./SocketCommandQueue.ts";
import {
    getClientDeviceInfo,
    mergeMonitorInfo,
    monitorIdentityOf,
    type ClientDeviceInfo,
    type MonitorInfo,
} from "../utils/DeviceInfo.ts";
import {integrationManager} from "../integrations/IntegrationManager.ts";
import type {ConnectionSupervisor} from "./ConnectionSupervisor.ts";

type SocketManagerDeps = {
    getChannelManager: () => ChannelManager;
    getRuleManager: () => RuleManager;
    getPlaylistManager: () => PlaylistManager;
};

export class SocketManager {
    private readonly url: string;
    private socket: Socket | null = null;
    private hasAuthenticated: boolean = false;
    private authenticationPromise: Promise<void> | null = null;
    private authenticationResolver: (() => void) | null = null;
    private authenticationRejector: ((reason?: unknown) => void) | null = null;
    private readonly updateQueueManager: UpdateQueueManager;
    public socketListener: SocketListener | null = null;
    public layoutManager: LayoutManager | null = null;
    private device_id: string | null = null;
    private deviceInfo: ClientDeviceInfo | null = null;
    /**
     * Latest known monitor state, accumulated from native `device:monitor:status`
     * reports. Rides along on the authenticate payload when known in time, and
     * is pushed as a standalone `device_info` event on every later change.
     */
    private monitorInfo: MonitorInfo | null = null;
    private monitorIdentity: string | null = null;
    private readonly ignoredMonitors = new Set<string>();
    private authenticateSent = false;
    public dependencies: SocketManagerDeps | null = null;
    public readonly commandQueue: SocketCommandQueue;
    private textCache: Map<string, string> | null = null;

    /* see docs/EXTRACTED_COMMENTS.md#c129 */
    public supervisor: ConnectionSupervisor | null = null;

    constructor(deps: SocketManagerDeps) {
        this.dependencies = deps;
        /* page origin on a real domain (PWA) → stored (auth page) → build default (Tauri/Electron/dev before setup) */
        this.url = resolveEnvironmentUrl();

        this.authenticationPromise = new Promise<void>((resolve, reject) => {
            this.authenticationResolver = resolve;
            this.authenticationRejector = reject;
        });
        this.updateQueueManager = new UpdateQueueManager();
        this.commandQueue = new SocketCommandQueue();
        this.textCache = new Map<string, string>();
    }

    /* see docs/EXTRACTED_COMMENTS.md#c130 */
    connect() {
        if (this.supervisor == null) {
            console.error("[SocketManager] connect() called before supervisor was attached");
            return;
        }
        if (this.layoutManager == null) {
            this.layoutManager = new LayoutManager(this.commandQueue);
        }
        this.supervisor.start();
    }

    /* see docs/EXTRACTED_COMMENTS.md#c131 */
    public adoptSocket(socket: Socket): void {
        this.socket = socket;
        this.commandQueue.bind(socket);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c132 */
    public adoptListener(listener: SocketListener): void {
        this.socketListener = listener;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c133 */
    public attachSupervisor(supervisor: ConnectionSupervisor): void {
        this.supervisor = supervisor;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c134 */
    kick(source: string = "unknown"): void {
        if (this.socket == null) {
            console.log(`[SocketManager] kick(${source}) — no socket yet, calling connect()`);
            this.connect();
            return;
        }
        if (this.socket.connected) {
            console.log(`[SocketManager] kick(${source}) — already connected, ignoring`);
            return;
        }
        console.log(`[SocketManager] kick(${source}) — requesting immediate supervisor retry`);
        this.supervisor?.requestImmediateRetry(source);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c515 */
    public recycle(reason: string): void {
        this.supervisor?.recycle(reason);
    }


    getSocket() {
        return this.socket;
    }

    disconnect() {
        if (this.socket == null) return;
        this.resetAuthenticationState();
        this.commandQueue.reset();
        const store = useStore();
        store.connection.state = ConnectionState.Disconnected;
        this.socket.disconnect();
    }

    async requestLayout() {
        if (this.layoutManager == null) return;
        await this.layoutManager.requestLayoutHash();
    }

    async requestLayoutBuild(data: any) {
        if (this.layoutManager == null) return;
        await this.layoutManager.requestLayoutBuild(data);
    }

    /* see docs/EXTRACTED_COMMENTS.md#c516 */
    async sendDevice(deviceId: string) {
        if (!deviceId) return;
        this.device_id = deviceId;
        if (this.socket == null) return;
        const info = await this.getDeviceInfo();
        // see docs/EXTRACTED_COMMENTS.md#c517
        const rl = integrationManager.getRuntimeLayer();
        const rl_version = integrationManager.getRuntimeVersion();
        console.log(`[SocketManager] Authenticating as ${deviceId}`);
        // Monitor state known by now travels with the authenticate payload, so
        // the backend has it for the very first DCS config message. Anything
        // that arrives later goes out as its own `device_info` event.
        const monitor_info = this.monitorInfo ?? undefined;
        this.authenticateSent = true;
        this.commandQueue.emitAuthenticate({ device_id: deviceId, ...info, rl, rl_version, monitor_info });
    }

    /**
     * Send the standalone `device_info` event. Queued while the command queue
     * is locked, so a report made between authenticate and its ack still lands
     * — right after authentication completes.
     */
    public sendDeviceInfo(info: ClientDeviceInfo): void {
        this.commandQueue.send("device_info", info);
    }

    /**
     * Fold a monitor report into what we know and forward it. Fields the report
     * omits keep their previous value, matching how the backend merges.
     *
     * Two filters matter here, because the native side polls every display on a
     * 5s timer: unchanged reports are dropped, and reports about a display other
     * than the first one seen are ignored — upstream `ClientInfo` carries a
     * single monitor, so alternating between panels would just thrash it.
     */
    public updateMonitorInfo(report: MonitorInfo): void {
        const identity = monitorIdentityOf(report);
        if (identity != null) {
            if (this.monitorIdentity == null) {
                this.monitorIdentity = identity;
            } else if (identity !== this.monitorIdentity) {
                if (!this.ignoredMonitors.has(identity)) {
                    this.ignoredMonitors.add(identity);
                    console.info(
                        `[SocketManager] Ignoring monitor ${identity}; already reporting ${this.monitorIdentity}.`,
                    );
                }
                return;
            }
        }

        const merged = mergeMonitorInfo(this.monitorInfo, report);
        if (this.monitorInfo != null && JSON.stringify(merged) === JSON.stringify(this.monitorInfo)) {
            return;
        }
        this.monitorInfo = merged;
        // Before authenticate goes out there is nothing to update yet; the
        // payload above will carry this snapshot.
        if (!this.authenticateSent) return;
        this.sendDeviceInfo({ monitor_info: merged });
    }

    /* see docs/EXTRACTED_COMMENTS.md#c518 */
    private async getDeviceInfo(): Promise<ClientDeviceInfo> {
        if (this.deviceInfo == null) {
            try {
                this.deviceInfo = await getClientDeviceInfo();
            } catch {
                this.deviceInfo = {};
            }
        }
        // The native layer's details (Tizen version, MAC, LAN IP) are read fresh: the integration
        // may still be probing on the first call, and the panel's address can change.
        return {...this.deviceInfo, ...await integrationManager.getDeviceInfo()};
    }

    async sendScreenDump(data: any, device_id: string) {
        if (this.socket == null) return;
        console.log(`[SocketManager] Sending screendump with device id ${device_id}.`)
        this.commandQueue.send("screendump", {
            url: data,
            device_id: device_id,
        });
    }


    /* see docs/EXTRACTED_COMMENTS.md#c519 */
    private sentSubscriptions = new Set<string>();
    private sentSubscriptionsSession = -1;

    private subscriptionLedger(): Set<string> {
        const session = this.commandQueue.sessionId;
        if (session !== this.sentSubscriptionsSession) {
            this.sentSubscriptionsSession = session;
            this.sentSubscriptions.clear();
        }
        return this.sentSubscriptions;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c520 */
    private layoutConfirmedSession = -1;

    /* see docs/EXTRACTED_COMMENTS.md#c521 */
    public confirmLayoutForSubscriptions(): void {
        this.layoutConfirmedSession = this.commandQueue.sessionId;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c522 */
    public isLayoutConfirmedForSubscriptions(): boolean {
        return this.layoutConfirmedSession === this.commandQueue.sessionId;
    }

    requestChannel(channelName: string, fields: number[] | null) {
        if (this.socket == null) return;
        // see docs/EXTRACTED_COMMENTS.md#c523
        if (!this.isLayoutConfirmedForSubscriptions()) return;
        const ledger = this.subscriptionLedger();
        const key = `${Number(channelName)}`;
        if (ledger.has(key)) {
            return;
        }
        ledger.add(key);
        console.log(`[SocketManager] Requesting subscription for channel ${channelName}`);
        this.commandQueue.send("channel_subscribe", {
            channel_id: Number(channelName),
            fields: fields ?? [],
            // see docs/EXTRACTED_COMMENTS.md#c524
            session_id: String(this.updateQueueManager.getSessionId()),
        });
    }

    removeChannel(channelName: string) {
        if (this.socket == null) return;
        this.subscriptionLedger().delete(`${Number(channelName)}`);
        this.commandQueue.send("channel_unsubscribe", {
            channel_id: Number(channelName),
        });
    }

    postClusterChannel(channel_id: number) {
        if (this.socket == null) return;
        this.commandQueue.send("cluster.channel", {
            channel: channel_id ?? 0,
        });
    }

    postRowUsage(count: number) {
        if (this.socket == null) return;
        this.commandQueue.send("cluster.rows", {
            amount: count,
        });
    }

    requestPlaylist(playlistName: string, callback: Function) {
        if (this.socket == null) return;
        const store = useStore();
        if (!store.connected) return;
        const playlist = store.playlistManager.getPlaylist(playlistName)
        if (playlist) {
            playlist.addCallback(callback);
            // see docs/EXTRACTED_COMMENTS.md#c728
            store.playlistManager.applyHideFlags();
            return;
        }
        store.playlistManager.addPlaylist(playlistName).addCallback(callback);
        console.log(`Requesting playlist ${playlistName}.`)
        this.commandQueue.send("request_playlist", {
            playlist_id: playlistName,
        });
    }

    requestRule(ruleName: string, callback: Function, row: number, channel: string, ownerId: string | null = null) {
        if (this.socket == null) return;
        const store = useStore();
        if (!store.connected) return;
        const existing = store.ruleManager.getRule(ruleName);
        if (existing) {
            existing.addCallback(callback, row, channel, ownerId);
            // see docs/EXTRACTED_COMMENTS.md#c783
            if (existing.getValue() == undefined && !store.ruleManager.isMissing(ruleName)) {
                this.commandQueue.send("request_rule", {rule_id: ruleName});
            }
            return;
        }
        const targetRule = store.ruleManager.addRule(ruleName);
        // see docs/EXTRACTED_COMMENTS.md#c525
        targetRule.addCallback(callback, row, channel, ownerId);
        console.log(`Requesting rule ${ruleName}.`)
        this.commandQueue.send("request_rule", {
            rule_id: ruleName,
        });
    }

    requestPageUp() {
        if (this.socket == null) return;
        const store = useStore();
        if (!store.loaded) return;

        console.log(`Requesting next page`);
        this.commandQueue.send("request_page_action", {
            action_type: "NEXT",
            current_layout: store.layout,
        });
    }

    requestPageDown() {
        if (this.socket == null) return;
        const store = useStore();
        if (!store.loaded) return;

        console.log(`Requesting previous page`);
        this.commandQueue.send("request_page_action", {
            action_type: "PREVIOUS",
            current_layout: store.layout,
        });
    }

    requestButtonStatus(item: Component) {
        if (this.socket == null) return;
        console.log(`Requesting button status of ${item.props.id}`);
        this.commandQueue.send("request_button_status", {
            object_id: item.props.id,
            object_name: item.object_name,
            row: item.dynamic.row,
            channel: item.dynamic.key_channel ?? item.dynamic.channel
        });
    }

    requestButtonUpdate(item: Component, action: string) {
        if (this.socket == null) return;
        console.log(`Forwarding button update for ${item.props.id}`);
        this.commandQueue.send("send_button_status", {
            object_id: item.props.id,
            object_name: item.object_name,
            row: item.dynamic.row,
            channel: item.dynamic.key_channel ?? item.dynamic.channel,
            action: action
        });
    }


    sendTextUpdate(item: Component, text: any, submit = false) {
        if (this.socket == null) return;
        const store = useStore();
        if (store.submitions.has(item.props.id) && !submit) {
            this.textCache?.set(item.props.id, text);
            return;
        }
        if (submit && text == null) {
            text = this.textCache?.get(item.props.id) ?? text;
        }
        console.log(`[SocketManager] Sending text update for ${item.props.id}`);
        this.commandQueue.send("send_text_update", {
            object_id: item.props.id,
            object_name: item.object_name,
            text
        });
    }

    /* see docs/EXTRACTED_COMMENTS.md#c526 */
    public queueUpdate(
        origin: UpdateOrigin,
        execute: (handle: UpdateContextHandle) => void,
        onDrop?: (err: Error) => void,
        channel?: string,
    ): void {
        this.updateQueueManager.queueUpdate(origin, execute, onDrop, channel);
    }

    public getQueueLength(): number {
        return this.updateQueueManager.queueLength;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c527 */
    public getUpdateQueueManager(): UpdateQueueManager {
        return this.updateQueueManager;
    }

    /**
     * Debug snapshot of the update queue for `window.DEBUG_PENDING()`.
     */
    public getUpdateQueueDebugSnapshot() {
        return this.updateQueueManager.getDebugSnapshot();
    }

    /* see docs/EXTRACTED_COMMENTS.md#c528 */
    public get sessionId(): number {
        return this.commandQueue.sessionId;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c529 */
    public setAuthenticated(authenticated: boolean) {
        this.hasAuthenticated = authenticated;

        if (authenticated) {
            if (this.authenticationResolver != null) {
                this.authenticationResolver();
            }
            this.authenticationPromise = null;
            this.authenticationResolver = null;
            this.authenticationRejector = null;
            this.commandQueue.unlock();
        } else {
            this.commandQueue.lock();
            if (this.authenticationPromise == null) {
                this.authenticationPromise = new Promise<void>((resolve, reject) => {
                    this.authenticationResolver = resolve;
                    this.authenticationRejector = reject;
                });
            }
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c530 */
    public resetAuthenticationState(): void {
        this.hasAuthenticated = false;
        // The next authenticate payload carries the current monitor snapshot
        // again, so don't also queue a `device_info` for it.
        this.authenticateSent = false;
        this.commandQueue.lock();
        if (this.authenticationRejector != null) {
            this.authenticationRejector(new Error("auth_session_reset"));
        }
        this.authenticationResolver = null;
        this.authenticationRejector = null;
        this.authenticationPromise = new Promise<void>((resolve, reject) => {
            this.authenticationResolver = resolve;
            this.authenticationRejector = reject;
        });
    }

    public getAuthenticationPromise(): Promise<void> | null {
        return this.authenticationPromise;
    }
    public isAuthenticated(): boolean {
        return this.hasAuthenticated;
    }
    public getDeviceId(): string | null {
        return this.device_id
    }

    public clearDeviceId(): void {
        this.device_id = null;
    }

    public clear(): void {
        this.updateQueueManager.clear();
        this.commandQueue.clear();
    }

    public getURL(): string {
        return this.url;
    }

    /* see docs/EXTRACTED_COMMENTS.md#c531 */
    public prepareNewSession(): void {
        this.commandQueue.reset();
        this.resetAuthenticationState();
    }
}
